const text = value => String(value ?? '').trim()
const upper = value => text(value).toUpperCase()
const number = value => value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value)
const close = (a, b, tolerance = 0.00001) => a != null && b != null && Math.abs(a - b) <= tolerance
const sum = (rows, field) => rows.every(row => number(row[field]) != null)
  ? rows.reduce((total, row) => total + Number(row[field]), 0) : null
const key = (owner, account, order) => JSON.stringify([owner, text(account), text(order)])
const entryRoles = new Set(['ENTRY', 'TARGET_ENTRY', 'RUNNER_ENTRY'])
// Legacy history has no verified contract multiplier (including adjusted options).
const multiplier = type => ['stock', 'equity', 'etf'].includes(type) ? 1 : null

function inspectTrade(trade, fills, refs, projects) {
  const entries = fills.filter(e => e.execution_type === 'entry')
  const candidates = new Set()
  let reason = null
  for (const entry of entries) {
    const owners = refs.get(key(trade.user_id, entry.account_number, entry.order_id)) || new Set()
    owners.forEach(id => candidates.add(id))
    if (owners.size !== 1) reason = 'Entry order has missing or conflicting setup attribution.'
  }
  if (!entries.length) reason = 'Entry executions are missing.'
  if (candidates.size !== 1) reason ||= 'Entries do not identify one setup.'
  const setupId = [...candidates][0]
  const project = projects.get(setupId)
  if (project && (upper(project.symbol) !== upper(trade.symbol)
    || (text(project.direction) && text(project.direction).toLowerCase() !== trade.side))) {
    reason = 'Setup instrument or direction does not match the trade.'
  }
  const identities = new Set()
  const sourceIds = new Set()
  for (const fill of fills) {
    const isEntry = fill.execution_type === 'entry'
    const expectedSide = (trade.side === 'long') === isEntry ? 'buy' : 'sell'
    if (!fill.id || identities.has(fill.id)) reason = 'Duplicate or unidentified execution.'
    identities.add(fill.id)
    const sourceId = fill.execution_id || fill.schwab_activity_id
    if (sourceId) {
      const sourceKey = key(fill.user_id, fill.account_number, sourceId)
      if (sourceIds.has(sourceKey)) reason = 'Duplicate source execution.'
      sourceIds.add(sourceKey)
    }
    if (fill.user_id !== trade.user_id || fill.account_number !== trade.account_number
      || upper(fill.symbol) !== upper(trade.symbol) || fill.side !== expectedSide
      || !['entry', 'exit', 'partial_exit'].includes(fill.execution_type)
      || !(number(fill.quantity) > 0) || number(fill.price) == null || !(number(fill.price) >= 0)) {
      reason = 'Execution identity, direction or quantity does not match the trade.'
    }
    if (!isEntry) {
      const owners = refs.get(key(trade.user_id, fill.account_number, fill.order_id))
      if (owners?.size && (owners.size !== 1 || !owners.has(setupId))) {
        reason = 'Exit order belongs to another setup.'
        owners.forEach(id => candidates.add(id))
      }
    }
  }
  const exits = fills.filter(e => ['exit', 'partial_exit'].includes(e.execution_type))
  const entered = sum(entries, 'quantity')
  const exited = sum(exits, 'quantity')
  const remaining = number(trade.quantity_open)
  if (!close(entered, number(trade.quantity_total)) || remaining == null || remaining < 0
    || !close(entered - exited, remaining)
    || !['long', 'short'].includes(trade.side)
    || !['closed', 'open', 'partial'].includes(trade.status)
    || (trade.status === 'closed' ? remaining !== 0 : !(remaining > 0))) {
    reason = 'Recorded quantities or status do not balance with executions.'
  }
  let running = 0
  const timeline = [...fills].sort((a, b) => {
    const difference = Date.parse(a.executed_at) - Date.parse(b.executed_at)
    return difference || Number(b.execution_type === 'entry') - Number(a.execution_type === 'entry')
  })
  for (const fill of timeline) {
    running += Number(fill.quantity) * (fill.execution_type === 'entry' ? 1 : -1)
    if (!Number.isFinite(Date.parse(fill.executed_at)) || running < -0.00001) {
      reason = 'Execution timing or running quantity needs reconciliation.'
    }
  }
  if (reason) return { reason, candidates }

  // Attribution and accounting are separate. A valid order link is not a
  // statement reconciliation or proof that all broker history was captured.
  const dataIssues = []
  const entryValue = entries.reduce((total, e) => total + Number(e.quantity) * Number(e.price), 0)
  const exitValue = exits.reduce((total, e) => total + Number(e.quantity) * Number(e.price), 0)
  const mult = multiplier(trade.asset_type)
  if (mult == null) dataIssues.push('Instrument multiplier needs verification.')
  if (!close(entryValue / entered, number(trade.avg_entry_price), 0.0001)
    || (exited > 0 && !close(exitValue / exited, number(trade.avg_exit_price), 0.0001))) {
    dataIssues.push('Recorded average prices differ from executions.')
  }
  const commission = sum(fills, 'commission')
  const fees = sum(fills, 'fees')
  if (!close(commission, number(trade.commission), 0.011) || !close(fees, number(trade.fees), 0.011)) {
    dataIssues.push('Fees are missing or differ from executions.')
  }
  if (trade.status === 'closed' && mult != null) {
    const gross = (exitValue - entryValue) * mult * (trade.side === 'short' ? -1 : 1)
    if (!close(gross, number(trade.gross_pnl), 0.011)
      || commission == null || fees == null || !close(gross - commission - fees, number(trade.net_pnl), 0.011)) {
      dataIssues.push('Recorded P&L differs from execution cashflows.')
    }
  }
  return { setupId, dataIssues, entryValue, exitValue }
}

function aggregate(trades) {
  const total = sum(trades, 'quantity_total')
  const remaining = sum(trades, 'quantity_open')
  const closed = trades.every(t => t.status === 'closed')
  const allOpen = trades.every(t => t.status === 'open' && Number(t.quantity_open) === Number(t.quantity_total))
  const entered = trades.reduce((n, t) => n + Number(t.quantity_total) * Number(t.avg_entry_price), 0)
  const exitedQuantity = total - remaining
  const exited = trades.reduce((n, t) => n + (Number(t.quantity_total) - Number(t.quantity_open)) * Number(t.avg_exit_price || 0), 0)
  const first = trades[0]
  const fills = trades.flatMap(t => t.executions)
  const byInstant = (a, b) => Date.parse(a) - Date.parse(b)
  const entryAt = fills.filter(e => e.execution_type === 'entry').map(e => e.executed_at).sort(byInstant)[0]
  const exitAt = closed ? fills.filter(e => e.execution_type !== 'entry').map(e => e.executed_at).sort(byInstant).at(-1) : null
  // Date filters follow the existing Personal trade ledger's Chicago day.
  const ledgerDate = value => value ? new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(value)) : null
  const dataIssues = [...new Set(trades.flatMap(t => t.dataIssues))]
  const net = !closed || dataIssues.length ? null : sum(trades, 'net_pnl')
  const basis = entered * multiplier(first.asset_type)
  return {
    symbol: first.symbol, side: first.side, asset_type: first.asset_type,
    status: closed ? 'closed' : allOpen ? 'open' : 'partial',
    quantity_total: total, quantity_open: remaining,
    avg_entry_price: entered / total, avg_exit_price: exitedQuantity > 0 ? exited / exitedQuantity : null,
    commission: sum(trades, 'commission'), fees: sum(trades, 'fees'),
    net_pnl: net, gross_pnl: dataIssues.length ? null : sum(trades, 'gross_pnl'),
    pnl_percent: closed && net != null && basis > 0 ? net / basis * 100 : null,
    entry_date: ledgerDate(entryAt), entry_time: null, entry_at: entryAt,
    exit_date: ledgerDate(exitAt), exit_time: null, exit_at: exitAt,
    // Legacy risk fields can be edited after the trade. Do not invent original setup risk.
    risk_amount: null, r_multiple: null, stop_loss: null,
    duration_minutes: exitAt ? Math.floor((Date.parse(exitAt) - Date.parse(entryAt)) / 60000) : null,
    dataIssues, performanceEligible: closed && net != null && !dataIssues.length,
  }
}

/** Pure, read-only projection. Never group by ticker, timestamp, playbook or legacy trade_id. */
export function buildSetupTradeLog({ trades = [], executions = [], projects = [], userId }) {
  if (!userId) return { groups: [], unresolved: [] }
  const byProject = new Map(projects.filter(p => p.user_id === userId && p.is_draft === false).map(p => [p.id, p]))
  const refs = new Map()
  for (const project of byProject.values()) {
    for (const order of Array.isArray(project.schwab_order_ids) ? project.schwab_order_ids : []) {
      if (!text(order.accountNumber) || !text(order.orderId)) continue
      // Index exits too for cross-setup conflict detection; only entry roles
      // are permitted to establish attribution below.
      const ref = key(userId, order.accountNumber, order.orderId)
      if (!refs.has(ref)) refs.set(ref, new Set())
      refs.get(ref).add(project.id)
    }
  }
  const entryRefs = new Map()
  for (const project of byProject.values()) {
    for (const order of Array.isArray(project.schwab_order_ids) ? project.schwab_order_ids : []) {
      if (text(order.role) && !entryRoles.has(upper(order.role))) continue
      if (!text(order.accountNumber) || !text(order.orderId)) continue
      const ref = key(userId, order.accountNumber, order.orderId)
      // Preserve conflicts even if the other reference is an exit.
      entryRefs.set(ref, refs.get(ref))
    }
  }
  const byTrade = new Map()
  const sourceTrades = new Map()
  const duplicateTrades = new Set()
  const activeTrades = trades.filter(t => t.user_id === userId && t.status !== 'duplicate')
  const activeIds = new Set(activeTrades.map(t => t.id))
  for (const fill of executions) {
    // Reconciliation retains superseded records and their source evidence.
    // Those records must not invalidate the surviving canonical trade.
    if (!activeIds.has(fill.trade_id)) continue
    if (!byTrade.has(fill.trade_id)) byTrade.set(fill.trade_id, [])
    byTrade.get(fill.trade_id).push(fill)
    const sourceId = fill.schwab_activity_id || fill.execution_id
    if (sourceId && fill.user_id === userId) {
      const sourceKey = JSON.stringify([fill.user_id, fill.account_number, fill.symbol, fill.order_id, sourceId])
      const previous = sourceTrades.get(sourceKey)
      if (previous && previous !== fill.trade_id) {
        duplicateTrades.add(previous)
        duplicateTrades.add(fill.trade_id)
      }
      sourceTrades.set(sourceKey, fill.trade_id)
    }
  }
  const unresolved = []
  const grouped = new Map()
  const affected = new Set()
  for (const trade of activeTrades) {
    const fills = byTrade.get(trade.id) || []
    const result = inspectTrade(trade, fills, entryRefs, byProject)
    if (duplicateTrades.has(trade.id)) {
      result.reason = 'Source executions are duplicated across trade records.'
      result.candidates ||= new Set(result.setupId ? [result.setupId] : [])
    }
    // Check known exits against all tracked roles.
    for (const fill of fills.filter(e => e.execution_type !== 'entry')) {
      const owners = refs.get(key(userId, fill.account_number, fill.order_id))
      if (result.setupId && owners?.size && (owners.size !== 1 || !owners.has(result.setupId))) {
        result.reason = 'Exit order belongs to another setup.'
        result.candidates = new Set([result.setupId, ...owners])
      }
    }
    if (result.reason) {
      result.candidates?.forEach(id => affected.add(id))
      unresolved.push({ ...trade, attributionReason: result.reason })
      continue
    }
    if (!grouped.has(result.setupId)) grouped.set(result.setupId, [])
    grouped.get(result.setupId).push({ ...trade, dataIssues: result.dataIssues, executions: fills })
  }
  const groups = [...grouped].map(([id, members]) => {
    const group = aggregate(members)
    if (affected.has(id) || new Set(members.map(t => `${t.symbol}:${t.side}:${t.asset_type}`)).size !== 1) {
      group.dataIssues.push('Other linked records need attribution or quantity reconciliation.')
      group.performanceEligible = false
      group.net_pnl = null
      group.pnl_percent = null
      group.status = 'unresolved'
    }
    const accounts = [...new Set(members.map(t => t.account_number))].map(account => {
      const accountTrades = members.filter(t => t.account_number === account)
      return { ...aggregate(accountTrades), account_number: account, trades: accountTrades }
    })
    return { ...group, id, project: byProject.get(id), trades: members, accounts,
      setupLabel: byProject.get(id).setup_pattern || byProject.get(id).setup_type || 'Unnamed setup',
      executions: members.flatMap(t => t.executions).sort((a, b) => Date.parse(a.executed_at) - Date.parse(b.executed_at)) }
  })
  return { groups, unresolved }
}

export function setupTradeStats(groups) {
  const eligible = groups.filter(g => g.performanceEligible && g.status === 'closed')
  const wins = eligible.filter(g => g.net_pnl > 0)
  const losses = eligible.filter(g => g.net_pnl < 0)
  const winningPnl = wins.reduce((n, g) => n + g.net_pnl, 0)
  const losingPnl = losses.reduce((n, g) => n + g.net_pnl, 0)
  return {
    count: eligible.length, wins: wins.length, losses: losses.length,
    breakeven: eligible.length - wins.length - losses.length,
    netPnl: eligible.length ? winningPnl + losingPnl : null,
    winRate: eligible.length ? wins.length / eligible.length * 100 : null,
    profitFactor: losingPnl < 0 ? winningPnl / Math.abs(losingPnl) : winningPnl > 0 ? Infinity : null,
    avgWinLossRatio: losses.length
      ? (wins.length ? winningPnl / wins.length : 0) / Math.abs(losingPnl / losses.length)
      : wins.length ? Infinity : null,
  }
}
